/**
 * Two small, MSAL-free decisions the sign-in code needs before MSAL loads.
 *
 * THE REDIRECT BRIDGE (msal-browser 5). A silent token request — ssoSilent, or
 * an acquireTokenSilent with no refresh token to use — opens a hidden iframe to
 * the tenant, which redirects it back to our registered redirect URI (the
 * portal root) with the response in the URL fragment. In msal-browser 4 the
 * opener read that fragment off the frame itself; in 5 it does not: the page
 * loaded INSIDE the frame must announce the response over a BroadcastChannel by
 * calling broadcastResponseToMainFrame() from '@azure/msal-browser/redirect-bridge',
 * or the opener waits out `system.iframeBridgeTimeout` (10 s) and throws
 * `timed_out`. The portal never called it. So every silent iframe request in
 * this app timed out, deterministically, since the day it shipped: the gate
 * showing "every single time" in the first embedded test (2026-08-27) and the
 * "timed_out" on the Business Central pane's gate (2026-09-02) were both this,
 * not cookies and not the tenant. main.tsx now asks isHiddenAuthFrame() first
 * and, in the frame, runs the bridge INSTEAD of the portal: the frame exists for
 * one message and is torn down by its opener the moment it arrives.
 *
 * Detection is deliberately strict — inside a frame AND an OAuth response
 * (`state` plus `code` / `error` / `ear_jwe`) in the fragment or the query —
 * because the portal legitimately lives in an iframe too (the BC pane), on URLs
 * that carry BC's own parameters (embed, user, msg, …) and never these.
 */

const RESPONSE_KEYS = ['code', 'error', 'ear_jwe'] as const;

/** An OAuth authorization response, as the tenant writes it into a URL. */
export function hasAuthResponse(hash: string, search: string): boolean {
  for (const raw of [hash, search]) {
    const params = new URLSearchParams(raw.replace(/^[#?]/, ''));
    if (params.has('state') && RESPONSE_KEYS.some((key) => params.has(key))) return true;
  }
  return false;
}

/** This window is MSAL's hidden iframe, back from the tenant with an answer. */
export function isHiddenAuthFrame(): boolean {
  let framed: boolean;
  try {
    framed = window.self !== window.top;
  } catch {
    // A cross-origin top throws on access — which only happens when framed.
    framed = true;
  }
  return framed && hasAuthResponse(window.location.hash, window.location.search);
}

/**
 * Announce the tenant's answer to the window that opened this frame, and do
 * nothing else. Imported on demand so the frame never carries the portal — it
 * loads the entry, this module, the bridge and the shared MSAL chunk, and none
 * of React, Fluent or the pages.
 */
export async function runAuthBridge(): Promise<void> {
  const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge');
  await broadcastResponseToMainFrame();
}

/**
 * Was this popup opened by the portal itself? Pure, so it can be tested; the
 * caller (auth.ts openedByPortal) supplies the live opener on the FIRST load
 * and remembers the answer, per nonce, across the tenant round trip.
 *
 * Only a same-origin opener is readable — a cross-origin opener throws on
 * `location` access, and that throw IS the answer. No opener, no trust: the
 * tenant's pages sever the opener only AFTER the round trip, by which time the
 * first load's answer is what counts, and window.open always sets it on the
 * first load. A referrer is deliberately not consulted — MSAL's own return hop
 * is a same-origin location.replace, whose referrer is this origin for a popup
 * anybody opened.
 */
export function decideOpenedByPortal(opener: Window | null, origin: string): boolean {
  if (!opener) return false;
  try {
    return opener.location.origin === origin;
  } catch {
    return false;
  }
}
